//
// Lets user choose target discussion
//
// Discussion can be identified based on 3 parameters:
//  - dspartGcat: list of groupCategoryCode's participating
//  - dspartGlbl: list of groupLabel's participating
//  - dspartId: list of crUid's participating always (not because of category code/label)
//
// dscdDflt is set for a discussion going to the case default category staff
//  (-> follows pxCaseCategoryCode choice)
//
// alertInfo: message to get alert info from
//

import PropTypes from 'prop-types';
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

import {
  useSingleCase,
  useTownAssets,
  useMedia,
} from '../../../stores';
import {
  displayJson, logError,
} from '../../../utils';
import { Column, Row } from '../../display';
import {
  Checkbox,
  TextInput,
} from '../../input';
import { useIsMounted } from '../../React';
import {
  ChatAlertContentPoliceSketch,
} from '../chat';

const maxMessageLength = 200;
const policeSketchSelectableFields = new Set(
  'fname',
  'lname',
  'age',
  'gender',
);

const idToDescription = {
  fname: 'firstName',
  lname: 'lastName',
  age: 'age',
  gender: 'gender',
  pic: 'picture',
};
const defaultSelection = {
  fname: true,
  lname: false,
  age: true,
  gender: true,
  pic: false,
};

function CaseCreateAlert(props) {
  const {
    alertInfo,
    cancelAlert,
  } = props;
  const { thisCase } = useSingleCase();
  const { media } = useMedia();
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { getMedia } = media;
  const {
    msgId,
    sketch,
    qstnId,
    msgFileCnt,
  } = alertInfo;
  const {
    ready,
    csid,
    piid,
    createAlert,
    csLat,
    csLng,
  } = thisCase;
  const [fieldSelection, setFieldSelection] = useState([]);
  const [alrtTxt, setAlrtTxt] = useState(translate('lookingForThisPerson'));
  const [pic, setPic] = useState(msgFileCnt
    ? getMedia({
        csid, piid, msgId, type: 'img',
      })
    : undefined);
  const isMountedRef = useIsMounted();
  const [zoneInfo, setZoneInfo] = useState({
    type: 'radius',
    radiusM: 200,
  });
  const picPending = useMemo(
    () => ((!!msgFileCnt) && (!pic)),
    [msgFileCnt, pic],
  );

  useEffect(
    () => {
      if (ready && (!fieldSelection.length)) {
        if (qstnId === 'policeSketch') {
          const newFieldSelection = Object.keys(sketch)
            .filter((key) => (policeSketchSelectableFields.has(key)))
            .map((key) => ({
              id: key,
              label: translate(idToDescription[key]),
              selected: defaultSelection[key],
              value: sketch[key],
            }));

          if (msgFileCnt) {
            newFieldSelection.push({
              id: 'pic',
              label: translate(idToDescription.pic),
              selected: defaultSelection.pic,
            });
          }
          setFieldSelection(newFieldSelection);
        } else {
          logError(
            `CaseCreateAlert alertInfo=${displayJson(alertInfo)}`,
            'CaseCreateAlert.invalidAlertInfo',
            {
              qstnId,
              alertInfoKeys: alertInfo && Object.keys(alertInfo),
            },
          );
        }
      }
    },
    [translate, ready, fieldSelection, alertInfo, sketch, qstnId, msgFileCnt],
  );

  const fieldsSelected = useMemo(
    () => (
      fieldSelection
        .filter((field) => (field.selected))
        .reduce(
          (prev, curr) => {
            const ret = { ...prev };
            ret[curr.id] = curr.value;
            if (curr.id === 'pic') ret[curr.id] = pic;
            return (ret);
          },
          {},
        )
    ),
    [fieldSelection, pic],
  );

  useEffect(
    () => {
      if (msgFileCnt && (!pic)) {
        console.log('CaseCreateAlert get pic');
        getMedia(
          {
            csid,
            piid,
            msgId,
            type: 'img',
          },
          (err, newPic) => {
            if (err) {
              console.log(`CaseCreateAlert err=${err}`);
            } else if (isMountedRef.current) {
              setPic(newPic);
            } else {
              console.log('CaseCreateAlert isMountedRef false');
            }
          },
        );
      }
    },
    [pic, msgFileCnt, csid, piid, getMedia, msgId, isMountedRef],
  );

  const handleCancel = () => {
    cancelAlert();
  };

  const handleComposeChange = useCallback(
    (event) => {
      if ((!event.target)
            || ((event.target.value)
                && (event.target.value.length > maxMessageLength))
      ) return;

      setAlrtTxt(event.target.value);
    },
    [],
  );

  const onChangeSel = useCallback(
    (event) => {
      const key = event.currentTarget.id;
      setFieldSelection((currentSel) => (currentSel
        .map((field) => {
          if (field.id !== key) return field;
          return ({
            ...field,
            selected: !field.selected,
          });
        })));
    },
    [],
  );

  const onChangeZone = useCallback(
    (event) => {
      const { id: key, value } = event.target;
      console.log(`Click on ${key}`);
      switch (key) {
        case 'radius':
        case 'poly':
        case 'townWide':
          setZoneInfo((curr) => ({
            ...curr,
            type: key,
          }));
          break;

        case 'radiusM':
          setZoneInfo((curr) => ({
            ...curr,
            radiusM: value,
          }));
          break;

        default:
          setZoneInfo({
            type: 'radius',
            radiusM: 200,
          });
          break;
      }
    },
    [],
  );

  const handleConfirm = useCallback(
    async () => {
      const { pic: picSelected, ...otherFieldsSelected } = fieldsSelected;

      const alrtForm = { qstnId };
      if (qstnId === 'policeSketch') alrtForm.sketch = otherFieldsSelected;

      let sendZoneInfo;
      if (zoneInfo.type === 'radius') {
        sendZoneInfo = {
          alrtLat: csLat,
          alrtLng: csLng,
          alrtRadiusM: zoneInfo.radiusM,
          alrtPolyLngLat: undefined,
        };
      } else if ((zoneInfo.type === 'poly') || (zoneInfo.type === 'townWide')) {
        sendZoneInfo = {
          alrtLat: undefined,
          alrtLng: undefined,
          alrtRadiusM: undefined,
          alrtPolyLngLat: undefined,
        };
      }

      await createAlert({
        csid,
        alrtEDatim: undefined,
        alrtAnon: 0,
        alrtTxt,
        alrtForm,
        mediaMsgId: (picSelected) ? msgId : undefined,
        ...sendZoneInfo,
      });

      cancelAlert();
    },
    [
      fieldsSelected, qstnId, zoneInfo.type, zoneInfo.radiusM,
      createAlert, csid, alrtTxt, msgId, cancelAlert, csLat, csLng,
    ],
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-popup max-w-140 w-full mx-4 max-h-screen overflow-y-auto">
        {/* Dialog Title */}
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-pxTxt">
            {translate('createAnAlert')}
          </h2>
        </div>

        {/* Dialog Content */}
        <div className="px-6 py-4 space-y-6">
          <Column gap={4}>
            <span className="font-bold">{`${translate('selectInfoToSend')}:`}</span>

            <div className="grid grid-cols-2 gap-1.25">
              {fieldSelection.map((field) => (
                <Row start key={field.id} className="w-full mb-3">
                  <Checkbox
                    id={field.id}
                    checked={field.selected}
                    className="self-center p-0.5"
                    onChange={onChangeSel}
                  />
                  <span className="ml-2">{field.label}</span>
                </Row>
              ))}
            </div>

            <div>
              <span className="italic">{`${translate('message')}:`}</span>
              <TextInput
                id="compose"
                placeholder={`${translate('message')}...`}
                multiline
                fullWidth
                onChange={handleComposeChange}
                value={alrtTxt}
                hideLabel
              />
            </div>

            <div className="py-1 px-5">
              <div className="w-full h-0.5 bg-pxAccentBlue opacity-30" />
            </div>

            <Column gap={3} crossStart>
              <span className="font-bold">{translate('broadcastArea')}</span>

              <Row start className="w-full">
                <Checkbox
                  id="radius"
                  checked={zoneInfo.type === 'radius'}
                  className="self-center p-0.5"
                  onChange={onChangeZone}
                />
                <span className="ml-2">{translate('withinRadius')}</span>
                <TextInput
                  id="radiusM"
                  value={zoneInfo.radiusM}
                  onChange={onChangeZone}
                  fillType="off"
                  widthChars={6}
                  disabled={zoneInfo.type !== 'radius'}
                  className="ml-2"
                  hideLabel
                />
                <span className="ml-2">{translate('meters')}</span>
              </Row>

              <Row start className="w-full">
                <Checkbox
                  id="poly"
                  checked={zoneInfo.type === 'poly'}
                  className="self-center p-0.5"
                  onChange={onChangeZone}
                />
                <span className="ml-2">{translate('withinPolygon')}</span>
              </Row>

              <Row start className="w-full">
                <Checkbox
                  id="townWide"
                  checked={zoneInfo.type === 'townWide'}
                  className="self-center p-0.5"
                  onChange={onChangeZone}
                />
                <span className="ml-2">{translate('overEntireTown')}</span>
              </Row>
            </Column>

            <div className="py-1 px-5">
              <div className="w-full h-0.5 bg-pxAccentBlue opacity-30" />
            </div>

            <div>
              <span className="font-bold">{`${translate('alertMessagePreview')}:`}</span>
              <div className="p-3 bg-pxLightBlue shadow-innerTxt mt-3">
                <div className="rounded-3 p-3">
                  <ChatAlertContentPoliceSketch
                    text={alrtTxt}
                    picPending={picPending}
                    {...fieldsSelected}
                  />
                </div>
              </div>
            </div>
          </Column>
        </div>

        {/* Dialog Actions */}
        <div className="px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-pxPrimary border border-pxPrimary rounded hover:bg-pxBgSubtle transition-colors"
          >
            {translate('cancel')}
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 bg-pxPrimary text-white rounded hover:bg-pxPrimaryFocus transition-colors"
          >
            {translate('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

CaseCreateAlert.propTypes = {
  alertInfo: PropTypes.shape({
    msgId: PropTypes.number,
    author: PropTypes.string,
    qstnId: PropTypes.string,
    msgFileCnt: PropTypes.number,
    sketch: PropTypes.shape({}),
  }).isRequired,
  cancelAlert: PropTypes.func.isRequired,
};

export default CaseCreateAlert;
